import LoggingAPI from "@/api/logging";
import UserApi from "@/api/users/users";
import { FormTitle } from "@/components/Forms/components/MonolithFormComponents";
import UserActionModal from "@/components/Modals/UserActionModal";
import { usePermissions } from "@/hooks/usePermissions";
import { OnPremUser } from "@/types/Users";

import { Switch, Tooltip } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import { FC, useState } from "react";
import { useTheme } from "styled-components";
import { InteractiveInfoIcon } from "./UserActionBlock";

interface MFAToggleBlockProps {
  userData: OnPremUser;
  onSubmit?: () => void;
}

const MFAToggleBlock: FC<MFAToggleBlockProps> = ({ userData, onSubmit }) => {
  const [showMFAEnableModal, setShowMFAEnableModal] = useState(false);

  const theme: any = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const handleSubmit = async () => {
    if (!userData?.email) {
      enqueueSnackbar(
        "An error occurred changing user password: invalid email.",
        {
          variant: "error",
        }
      );
      console.error("An error occurred changing user password: invalid email.");
      return;
    }
    await UserApi.updateMFA({
      email: userData.email,
      mfa_enabled: !!userData.mfa_enabled === false ? 1 : 0, // toggle on if 0 or null
    });

    LoggingAPI.createLog({
      user_id: currentUser.user_id,
      message: `user MFA reset: ${userData?.email}`,
      event: "user:change_MFA",
      object_type: "user",
    });

    onSubmit && onSubmit();
  };

  return (
    <>
      <div
        style={{
          margin: "35px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "2px",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "2px",
            }}
          >
            <FormTitle>MFA Settings</FormTitle>
            <Tooltip content="Enable/Disable MFA for this user.">
              <InteractiveInfoIcon
                size={18}
                color={theme.palette.primary.contrastText}
              />
            </Tooltip>
          </div>
          <div
            style={{
              fontSize: 13,
              color: theme.palette.text.secondary,
              margin: "3px 5px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
            }}
          >
            Enabled
            <Switch
              size="sm"
              onChange={() => {
                setShowMFAEnableModal(true);
              }}
              value={!!userData.mfa_enabled}
            />
          </div>
        </div>
      </div>

      <UserActionModal
        show={showMFAEnableModal}
        onCancel={() => setShowMFAEnableModal(false)}
        onClose={() => setShowMFAEnableModal(false)}
        onSubmit={handleSubmit}
        title={!!userData.mfa_enabled ? "Disable MFA" : "Enable MFA"}
        body={`Would you like to ${
          !!userData.mfa_enabled ? "disable" : "enable"
        } MFA for the user?
          `}
        buttonProps={{
          submitColor: !!userData.mfa_enabled ? "error" : "primary",
          submitText: !!userData.mfa_enabled ? "Disable MFA" : "Enable MFA",
        }}
      />
    </>
  );
};

export default MFAToggleBlock;
